import { useCallback, useLayoutEffect } from 'react'
import { v4 } from 'uuid'
import { EventTypes, ModelMeshClickListener } from '../state/store'
import { listensers } from './useEmitModelClickListenerV2'

const emptyArray: string[] = []

const defaultOptions: { enabled: boolean; guids: string | string[] | null } = {
  enabled: true,
  guids: emptyArray,
}

const useModelClickListenerV2 = (
  /**
   * When you pass an array of event types, always make sure to pass
   * an memoized array otherwise there are lots of rerenders.
   */
  eventType: EventTypes | EventTypes[],
  callback: ModelMeshClickListener,
  options: {
    enabled: boolean
    /**
     * Enable this callback while the multi-select modifier is held
     */
    enableOnMultiSelect?: boolean
    /**
     * Enable this callback even when ifc mode is active (working on ifc model)
     */
    enableOnIfcMode?: boolean
    /**
     * The guids which should be listened to, pass null if you want to execute the callback for all elements.
     */
    guids: string | string[] | null
  } = defaultOptions,
) => {
  const addListener = useCallback(
    (id: string, callback: ModelMeshClickListener) => {
      listensers.set(id, {
        listener: callback,
        guids: options.guids,
        eventType,
        enableOnMultiSelect: options.enableOnMultiSelect,
        enableOnIfcMode: options.enableOnIfcMode,
      })
    },
    [options.guids, options.enableOnMultiSelect, eventType, options.enableOnIfcMode],
  )

  const removeListener = useCallback((id: string) => {
    listensers.delete(id)
  }, [])

  useLayoutEffect(() => {
    if (!options.enabled) return

    const id = v4()

    addListener(id, callback)
    return () => removeListener(id)
  }, [addListener, callback, options.enabled, removeListener])
}

export default useModelClickListenerV2
